import React, { useEffect, useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useSelector } from 'react-redux';
import { IAppState } from '../../store/rootDuck';
import { getPublicDoc } from '../../crud/docs.crud';

const COOKIE_CONSENT_KEY = 'cookie_consent';

const useStyles = makeStyles()(theme => ({
  root: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 9999,
    backgroundColor: '#181c32',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 24,
    padding: '20px 40px',
    boxShadow: '0 -4px 24px rgba(0,0,0,0.18)',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      padding: '16px 20px',
      gap: 16,
    },
  },
  text: {
    fontSize: 14,
    lineHeight: 1.6,
    flex: 1,
    color: '#e0e0e8',
    '& strong': {
      color: '#fff',
      fontWeight: 600,
    },
    '& a': {
      color: '#7c97c0',
      textDecoration: 'underline',
      cursor: 'pointer',
    },
  },
  loader: {
    color: '#e0e0e8',
  },
  buttons: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    flexShrink: 0,
    minWidth: 240,
    [theme.breakpoints.down('md')]: {
      width: '100%',
      minWidth: 0,
    },
  },
  btnAccept: {
    backgroundColor: '#ff4200',
    color: '#fff',
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 22,
    height: 44,
    '&:hover': {
      backgroundColor: '#e63d00',
    },
  },
  btnDecline: {
    backgroundColor: 'transparent',
    color: '#e0e0e8',
    textTransform: 'none',
    fontWeight: 400,
    borderRadius: 22,
    height: 44,
    border: '1px solid rgba(255,255,255,0.3)',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.06)',
    },
  },
}));

const CookieBanner: React.FC = () => {
  const { classes } = useStyles();
  const isAuthorized = useSelector((state: IAppState) => state.auth.user != null);

  const [visible, setVisible] = useState(false);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAuthorized) return;
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (consent) return;

    setLoading(true);
    setVisible(true);
    getPublicDoc('cookie_policy')
      .then(res => {
        setText(res.data.data.text || '');
      })
      .catch(() => {
        setText('Мы используем файлы cookie');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [isAuthorized]);

  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
    setVisible(false);
  };

  const handleDecline = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'declined');
    setVisible(false);
  };

  if (!visible || isAuthorized) return null;

  return (
    <div className={classes.root}>
      <div className={classes.text}>
        {loading ? (
          <CircularProgress size={18} className={classes.loader} />
        ) : (
          <span dangerouslySetInnerHTML={{ __html: text }} />
        )}
      </div>
      <div className={classes.buttons}>
        <Button variant='contained' className={classes.btnAccept} onClick={handleAccept}>
          Принять все файлы cookie
        </Button>
        <Button variant='outlined' className={classes.btnDecline} onClick={handleDecline}>
          Отказаться от несущественных файлов cookie
        </Button>
      </div>
    </div>
  );
};

export default CookieBanner;
